/* ContractorSoft form fields — design spec §7 (flat label + soft gray shell)
   Payment gateway (NMI + Dejavoo): PaymentGatewayFields.css — imported in index.css */

:root {
  --cs-input-bg: #f7f8fa;
  --cs-input-bg-focus: #ffffff;
  --cs-input-border-default: #e5e7eb;
  --cs-input-border-hover: #d1d5db;
  --cs-input-border-focus: #2563eb;
  --cs-input-focus-ring: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

.cs-form-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
  box-sizing: border-box;
}

.cs-form-field--full {
  width: 100%;
  max-width: 100%;
}

.cs-field-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
  line-height: 1.3;
  font-family: inherit;
  margin: 0;
  padding: 0;
}

.cs-field-label--error {
  color: #dc3545;
}

.cs-field-label-required {
  color: #dc3545;
  font-weight: 700;
  margin-left: 2px;
}

/* Parent .text-blue-color / theme utilities must not paint the star navy */
.cs-field-label .cs-field-label-required,
.cs-rs__label .cs-field-label-required,
.cs-currency-label .cs-field-label-required,
.text-blue-color .cs-field-label .cs-field-label-required,
.text-blue-color .cs-rs__label .cs-field-label-required {
  color: #dc3545 !important;
}

.cs-input-shell {
  display: flex;
  align-items: stretch;
  position: relative;
  width: 100%;
  box-sizing: border-box;
  background-color: var(--cs-input-bg);
  border: 1px solid var(--cs-input-border-default);
  border-radius: 8px;
  /* Clip inner fill/autofill to the shell curve so corners stay flush with the border */
  overflow: hidden;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease,
    background-color 0.15s ease;
}

.cs-input-shell:hover:not(.cs-input-shell--disabled) {
  border-color: var(--cs-input-border-hover);
}

.cs-input-shell:focus-within:not(.cs-input-shell--disabled) {
  background-color: var(--cs-input-bg-focus);
  border-color: var(--cs-input-border-focus);
  box-shadow: var(--cs-input-focus-ring);
}

.cs-input-shell--error {
  border-color: #dc3545 !important;
}

.cs-input-shell--error:focus-within {
  box-shadow: var(--focus-ring-error);
}

.cs-input-shell--multiline {
  height: auto !important;
  min-height: 80px;
  align-items: flex-start;
  overflow: visible;
}

.cs-input-shell--disabled {
  opacity: 0.72;
  cursor: not-allowed;
  background-color: #e2e8f0;
}

.cs-input {
  flex: 1;
  min-width: 0;
  border: none;
  background: transparent;
  outline: none;
  font-size: 15px;
  line-height: 1.4;
  color: #4b5563;
  padding: 10px 14px;
  font-family: inherit;
  box-sizing: border-box;
}

.cs-input-shell:not(.cs-input-shell--multiline) .cs-input {
  height: 100%;
}

.cs-input-shell--multiline .cs-input {
  min-height: 72px;
  padding: 10px 14px;
  resize: vertical;
}

/*
 * Chrome autofill: override yellow fill with inset box-shadow.
 * Important: when blurred, :-webkit-autofill still matches — if we always use white,
 * the field stays white after leave while empty fields show shell grey. Idle must
 * use --cs-input-bg; only :focus-within uses --cs-input-bg-focus (white).
 */
.cs-input-shell:not(.cs-input-shell--multiline) .cs-input:-webkit-autofill,
.cs-input-shell:not(.cs-input-shell--multiline) .cs-input:-webkit-autofill:hover,
.cs-input-shell:not(.cs-input-shell--multiline) .cs-input:-webkit-autofill:active,
.cs-input-shell:not(.cs-input-shell--multiline) .cs-input:autofill {
  -webkit-box-shadow: 0 0 0 1000px var(--cs-input-bg) inset !important;
  box-shadow: 0 0 0 1000px var(--cs-input-bg) inset !important;
  -webkit-text-fill-color: #4b5563 !important;
  caret-color: #4b5563;
  transition: background-color 99999s ease-out 0s;
  background-color: var(--cs-input-bg) !important;
  background-image: none !important;
}

.cs-input-shell:focus-within:not(.cs-input-shell--multiline) .cs-input:-webkit-autofill,
.cs-input-shell:focus-within:not(.cs-input-shell--multiline) .cs-input:-webkit-autofill:hover,
.cs-input-shell:focus-within:not(.cs-input-shell--multiline) .cs-input:-webkit-autofill:focus,
.cs-input-shell:focus-within:not(.cs-input-shell--multiline) .cs-input:-webkit-autofill:active,
.cs-input-shell:focus-within:not(.cs-input-shell--multiline) .cs-input:autofill {
  -webkit-box-shadow: 0 0 0 1000px var(--cs-input-bg-focus) inset !important;
  box-shadow: 0 0 0 1000px var(--cs-input-bg-focus) inset !important;
  -webkit-text-fill-color: #4b5563 !important;
  background-color: var(--cs-input-bg-focus) !important;
  background-image: none !important;
}

/* Nuclear: every text-input shell renders identically in idle / hover state.
   Guards against parent-state quirks (autofill, browser hint chrome, etc.). */
.cs-input-shell:not(.cs-input-shell--multiline):not(.cs-input-shell--disabled):not(.cs-input-shell--error):not(:focus-within) {
  background-color: var(--cs-input-bg) !important;
  background-image: none !important;
}

.cs-input-shell:not(.cs-input-shell--multiline):not(.cs-input-shell--disabled) .cs-input {
  background-color: transparent !important;
  background-image: none !important;
}

.cs-input::placeholder {
  color: #9ca3af;
  opacity: 1;
}

.cs-input--right {
  text-align: right;
}

.cs-input--error-text {
  color: #dc3545 !important;
}

.cs-input--error-text::placeholder {
  color: #dc3545 !important;
}

select.cs-input {
  appearance: none;
  background-color: transparent;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%234b5563' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 16px;
  padding-right: 40px;
  cursor: pointer;
}

/*
 * Date & Time Format settings: timezone = MUI Autocomplete → plain .cs-input (inherits body = Inter).
 * Date / time = MUI TextField select → theme typography = Poppins (see index.js createTheme).
 * Force Poppins here so the timezone value matches the two selects next to it.
 */
.cs-form-field.datetime-format-autocomplete--contractor .cs-input,
.cs-form-field.datetime-format-autocomplete--donor .cs-input {
  font-family: "Poppins", sans-serif !important;
  font-weight: 400;
  font-size: 1rem;
  line-height: 1.4375;
  letter-spacing: 0.00938em;
  color: rgba(0, 0, 0, 0.87);
  -webkit-font-smoothing: antialiased;
}

.cs-form-field.datetime-format-autocomplete--contractor .cs-input:-webkit-autofill,
.cs-form-field.datetime-format-autocomplete--donor .cs-input:-webkit-autofill {
  -webkit-text-fill-color: rgba(0, 0, 0, 0.87);
  font-family: "Poppins", sans-serif !important;
}

.cs-input-adorn {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  color: #64748b;
}

.cs-input-adorn--start {
  padding-left: 12px;
}

.cs-input-adorn--end {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding-right: 8px;
  padding-left: 4px;
}

.cs-field-helper {
  font-size: 12px;
  color: #64748b;
  line-height: 1.2;
  margin: 0;
  padding: 0;
}

.cs-field-helper--error {
  color: #d32f2f;
}

/* Google Places clear (×) */
.cs-gpa-clear {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  margin: 0;
  padding: 0;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: #64748b;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
}

.cs-gpa-clear:hover {
  background: rgba(15, 23, 42, 0.06);
  color: #334155;
}

/* react-select wrapper (InputDropdown) — match .cs-form-field label + control rhythm */
.cs-rs__wrapper {
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
  box-sizing: border-box;
}

/* Dropdown panel — scroll list (menu is portaled; classPrefix cs-rs) */
.cs-rs__menu-list {
  scrollbar-width: thin;
  scrollbar-color: rgba(100, 116, 139, 0.55) transparent;
}

.cs-rs__menu-list::-webkit-scrollbar {
  width: 8px;
}

.cs-rs__menu-list::-webkit-scrollbar-track {
  background: rgba(241, 245, 249, 0.9);
  border-radius: 8px;
}

.cs-rs__menu-list::-webkit-scrollbar-thumb {
  background: rgba(148, 163, 184, 0.75);
  border-radius: 8px;
}

.cs-rs__menu-list::-webkit-scrollbar-thumb:hover {
  background: rgba(100, 116, 139, 0.85);
}

/* Open menu must stack above MUI Dialog/modals (react-select menuPortal) */
.cs-rs__menu-portal {
  z-index: 20000 !important;
}

.cs-rs__label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
  margin: 0;
  padding: 0;
  margin-bottom: 0;
  line-height: 1.3;
  font-family: inherit;
}

/* Error label must win over base .cs-rs__label color (same specificity, order) */
.cs-rs__label.cs-field-label--error {
  color: #dc3545;
}

/* react-select v5: single row — never wrap clear + chevron under the value (Profile / Address, etc.) */
.cs-rs__wrapper .cs-rs__control {
  flex-wrap: nowrap !important;
}
.cs-rs__wrapper .cs-rs__value-container {
  align-items: center !important;
  flex: 1 1 0% !important;
  min-width: 0 !important;
}
.cs-rs__wrapper .cs-rs__indicatorsContainer {
  flex-shrink: 0 !important;
}

.cs-rs__wrapper .cs-rs__placeholder,
.cs-rs__wrapper .cs-rs__single-value {
  margin: 0;
}

/* QuickDateFilter — same tokens as §7 form inputs */
.cs-rs__wrapper.quick-date-filter--like-quotes .cs-rs__control {
  background-color: var(--cs-input-bg) !important;
  border-color: var(--cs-input-border-default) !important;
  box-shadow: none !important;
}

.cs-rs__wrapper.quick-date-filter--like-quotes .cs-rs__control:hover {
  border-color: var(--cs-input-border-hover) !important;
}

.cs-rs__wrapper.quick-date-filter--like-quotes .cs-rs__control--is-focused {
  background-color: var(--cs-input-bg-focus) !important;
  border-color: var(--cs-input-border-focus) !important;
  box-shadow: var(--cs-input-focus-ring) !important;
}

/* JOB-3427: the previous values were hardcoded slate-700 / slate-500 hexes,
   which look fine on light cards but become unreadable on the dark-mode
   --bg-card surface. The text-primary / text-secondary tokens flip
   automatically with the admin-dark-active body class so the date filter
   on every report page picks up the right contrast in both themes. */
.cs-rs__wrapper.quick-date-filter--like-quotes .cs-rs__single-value {
  color: var(--text-primary) !important;
  font-weight: 500 !important;
}

.cs-rs__wrapper.quick-date-filter--like-quotes .cs-rs__placeholder {
  color: var(--text-secondary) !important;
}

.cs-rs__wrapper.quick-date-filter--like-quotes .cs-rs__dropdown-indicator {
  color: var(--text-secondary) !important;
}

.cs-rs__wrapper.quick-date-filter--like-quotes .cs-rs__input-container,
.cs-rs__wrapper.quick-date-filter--like-quotes .cs-rs__input {
  color: var(--text-primary) !important;
}

/* Currency / amount — single soft bar (AMOUNT reference: one fill, grey border, $ left / value right) */
.cs-currency-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
  box-sizing: border-box;
}

/* Label — match .cs-field-label (sentence case; same as Frequency / account fields) */
.cs-currency-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.3;
  font-family: inherit;
  letter-spacing: normal;
  text-transform: none;
  color: #64748b;
}

.cs-currency-label--error {
  color: #dc3545;
}

.cs-currency-label--disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.cs-currency-group {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  width: 100%;
  box-sizing: border-box;
  padding: 10px 14px;
  background: var(--cs-input-bg);
  border: 1px solid var(--cs-input-border-default);
  border-radius: 8px;
  position: relative;
  isolation: isolate;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease,
    background-color 0.15s ease;
}

.cs-currency-group:hover:not(.cs-currency-group--disabled):not(.cs-currency-group--error) {
  border-color: var(--cs-input-border-hover);
}

/* Legacy wrappers often add .border-blue-color — keep neutral bar, not blue bottom/split look */
.cs-currency-field.border-blue-color .cs-currency-group {
  border-color: var(--cs-input-border-default) !important;
}

.cs-currency-symbol {
  flex-shrink: 0;
  font-size: 16px;
  font-weight: 600;
  color: #475569;
  user-select: none;
  line-height: 1;
}

.cs-currency-input {
  flex: 1;
  min-width: 0;
  box-sizing: border-box;
  margin: 0;
  padding: 0;
  font-size: 15px;
  line-height: 1.4;
  font-weight: 500;
  color: #111827;
  background: transparent;
  border: none;
  outline: none;
  box-shadow: none;
  font-family: inherit;
  text-align: right;
  transition: color 0.15s ease;
  /* Keep huge -webkit autofill inset from compositing with parent border + focus ring */
  contain: paint;
}

/* Chrome autofill: inset fill clipped to input only (contain: paint). Match group idle fill. */
.cs-currency-input:-webkit-autofill,
.cs-currency-input:-webkit-autofill:hover,
.cs-currency-input:-webkit-autofill:focus {
  -webkit-box-shadow: 0 0 0 1000px var(--cs-input-bg) inset;
  -webkit-text-fill-color: #111827;
  transition: background-color 99999s ease-out 0s;
}

.cs-currency-group:focus-within:not(.cs-currency-group--disabled) .cs-currency-input:-webkit-autofill,
.cs-currency-group:focus-within:not(.cs-currency-group--disabled) .cs-currency-input:-webkit-autofill:hover,
.cs-currency-group:focus-within:not(.cs-currency-group--disabled) .cs-currency-input:-webkit-autofill:focus {
  -webkit-box-shadow: 0 0 0 1000px var(--cs-input-bg-focus) inset;
  -webkit-text-fill-color: #111827;
}

.cs-currency-group--error:focus-within:not(.cs-currency-group--disabled) .cs-currency-input:-webkit-autofill,
.cs-currency-group--error:focus-within:not(.cs-currency-group--disabled) .cs-currency-input:-webkit-autofill:focus {
  -webkit-box-shadow: 0 0 0 1000px var(--cs-input-bg) inset;
  -webkit-text-fill-color: #111827;
}

/* Explicit left only when Doller isRight={false} */
.cs-currency-input.cs-currency-input--align-left {
  text-align: left;
}

.cs-currency-input::placeholder {
  color: #9ca3af;
  opacity: 1;
}

.cs-currency-input:read-only {
  cursor: default;
  color: #334155;
}

.cs-currency-input--error-text {
  color: #dc3545 !important;
}

.cs-currency-input--error-text::placeholder {
  color: #f87171 !important;
}

/* Focus — :focus-within so $ + input behave like §7 (no React focus class).
   Avoid outer box-shadow ring here: it gets clipped by scroll/overflow ancestors and looks like
   “broken” corners. Border + fill match InputText; ring is optional elsewhere. */
.cs-currency-group:focus-within:not(.cs-currency-group--disabled):not(.cs-currency-group--error) {
  background-color: var(--cs-input-bg-focus);
  border-color: var(--cs-input-border-focus);
  box-shadow: none;
}

.cs-currency-group--error {
  border-color: #dc3545;
  background: var(--cs-input-bg);
}

.cs-currency-group--error:focus-within {
  border-color: #dc3545;
  box-shadow: none;
}

.cs-currency-end {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.cs-currency-group--disabled {
  opacity: 0.72;
  cursor: not-allowed;
  background: #e2e8f0;
}

.cs-currency-group--disabled .cs-currency-input {
  cursor: not-allowed;
  color: #64748b;
}

.cs-currency-group--disabled .cs-currency-symbol {
  color: #94a3b8;
}

/* Mobile Landscape (max-width: 767px) */
@media (max-width: 767px) {
  .cs-input-shell:not(.cs-input-shell--multiline) {
    min-height: 44px !important;
    height: 44px !important;
  }

  .cs-input:not(textarea) {
    font-size: 14px !important;
    padding: 10px 12px !important;
  }

  .cs-input-shell--multiline .cs-input {
    font-size: 14px !important;
    padding: 10px 12px !important;
  }

  .cs-input::placeholder {
    font-size: 14px !important;
  }

  .cs-field-label {
    font-size: 12px !important;
  }

  .cs-field-helper {
    font-size: 11px !important;
  }

  .cs-form-field,
  .cs-rs__wrapper {
    width: 100% !important;
    max-width: 100% !important;
  }
}

/* Small Mobile (max-width: 575px) */
@media (max-width: 575px) {
  .cs-input-shell:not(.cs-input-shell--multiline) {
    min-height: 44px !important;
    height: 44px !important;
  }

  .cs-input:not(textarea) {
    font-size: 13px !important;
    padding: 10px 11px !important;
  }

  .cs-input-shell--multiline .cs-input {
    font-size: 13px !important;
    padding: 10px 12px !important;
  }

  .cs-input::placeholder {
    font-size: 13px !important;
  }

  .cs-field-label {
    font-size: 12px !important;
  }

  .cs-field-helper {
    font-size: 10px !important;
  }
}

/* Extra Small Mobile (max-width: 424px) */
@media (max-width: 424px) {
  .cs-input-shell:not(.cs-input-shell--multiline) {
    min-height: 44px !important;
    height: 44px !important;
  }

  .cs-input:not(textarea) {
    font-size: 12px !important;
    padding: 10px !important;
  }

  .cs-input-shell--multiline .cs-input {
    font-size: 12px !important;
    padding: 10px 12px !important;
  }

  .cs-input::placeholder {
    font-size: 12px !important;
  }

  .cs-field-label {
    font-size: 12px !important;
  }

  .cs-field-helper {
    font-size: 10px !important;
  }
}

/* Materials line item — Hourly qty: row height matches selects + $ fields on all breakpoints */
.getproduct .gl-line-item-field.hour_squre_unit .cs-input-shell:not(.cs-input-shell--multiline) {
  min-height: 44px !important;
  height: 44px !important;
  max-height: 44px !important;
  box-sizing: border-box !important;
}
